import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import {
  Building2, Users, CreditCard, GitBranch, Mail, Phone, MapPin,
  Save, Plus, TrendingUp, FileText, Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

export default function OrgPortalPage({ user, onLogout }) {
  const [org, setOrg] = useState(null);
  const [branches, setBranches] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [newBranch, setNewBranch] = useState({ name: '', code: '', description: '' });
  const [savingOrg, setSavingOrg] = useState(false);

  const orgId = user?.organization_id;
  const isOrgAdmin = user?.role === 'org_admin';

  const fetchOrg = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API_URL}/api/organizations/my`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrg(data);
        setEditForm({
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          address: data.address || '',
          notes: data.notes || '',
          session_timeout_minutes: data.session_timeout_minutes ?? 60,
          mfa_enforcement_deadline: (data.mfa_enforcement_deadline || '').slice(0, 10),
        });
      }
    } catch { toast.error('Failed to load organization'); }
  }, [orgId]);

  const fetchBranches = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgId}/branches`, { headers: getHeaders() });
      if (res.ok) setBranches(await res.json());
    } catch (e) { console.error('fetchBranches failed', e); }
  }, [orgId]);

  const fetchUsers = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgId}/users`, { headers: getHeaders() });
      if (res.ok) setOrgUsers(await res.json());
    } catch (e) { console.error('fetchUsers failed', e); }
  }, [orgId]);

  useEffect(() => {
    Promise.all([fetchOrg(), fetchBranches(), fetchUsers()]).finally(() => setLoading(false));
  }, [fetchOrg, fetchBranches, fetchUsers]);

  const handleSaveOrg = async () => {
    setSavingOrg(true);
    try {
      const res = await fetch(`${API_URL}/api/organizations/my`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        toast.success('Organization updated');
        setEditing(false);
        fetchOrg();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Update failed');
      }
    } catch { toast.error('Update failed'); }
    setSavingOrg(false);
  };

  const handleCreateBranch = async () => {
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgId}/branches`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(newBranch)
      });
      if (res.ok) {
        toast.success('Branch created');
        setShowBranchDialog(false);
        setNewBranch({ name: '', code: '', description: '' });
        fetchBranches();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create branch');
      }
    } catch { toast.error('Failed to create branch'); }
  };

  if (!orgId) {
    return (
      <Layout user={user} onLogout={onLogout} activePage="org-portal">
        <div className="flex items-center justify-center min-h-[50vh]" data-testid="org-portal-no-org">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h2 className="text-lg font-semibold text-slate-700">No Organization</h2>
              <p className="text-sm text-slate-500 mt-2">You are not assigned to an organization. Contact your administrator.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} activePage="org-portal">
        <div className="flex items-center justify-center min-h-[50vh] text-slate-500">Loading...</div>
      </Layout>
    );
  }

  const creditPct = org ? ((org.remaining_credits / Math.max(org.prepaid_credits, 1)) * 100) : 0;

  return (
    <Layout user={user} onLogout={onLogout} activePage="org-portal">
      <div className="space-y-6" data-testid="org-portal-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="org-portal-title">{org?.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Organization Portal</p>
            {org?.id && (
              <div className="mt-2 inline-flex items-center gap-1.5 group" data-testid="org-id-display">
                <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Org ID</span>
                <code className="text-[11px] font-mono text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200">
                  {org.id}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(org.id);
                    toast.success('Organization ID copied');
                  }}
                  className="p-1 text-slate-400 hover:text-indigo-600 transition"
                  title="Copy ID"
                  data-testid="copy-org-id"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                </button>
              </div>
            )}
          </div>
          <Badge className={org?.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
            {org?.status}
          </Badge>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="org-stat-users">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Team Members</p>
                  <p className="text-2xl font-bold">{orgUsers.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="org-stat-branches">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Branches</p>
                  <p className="text-2xl font-bold">{branches.length}</p>
                </div>
                <GitBranch className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="org-stat-credits">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Credits Remaining</p>
                  <p className="text-2xl font-bold">{org?.remaining_credits?.toLocaleString()}</p>
                </div>
                <CreditCard className="w-8 h-8 text-emerald-500" />
              </div>
              <div className="mt-3 w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${creditPct > 25 ? 'bg-emerald-500' : creditPct > 10 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(creditPct, 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="org-stat-pricing">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Price per Scan</p>
                  <p className="text-2xl font-bold">${org?.pricing_per_scan}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organization Details */}
        <Card data-testid="org-details-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Contact information and settings</CardDescription>
            </div>
            {isOrgAdmin && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="org-edit-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {editing ? (
              <div className="space-y-4 max-w-lg">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      value={editForm.contact_email}
                      onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                      className="pl-9"
                      data-testid="org-edit-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      value={editForm.contact_phone}
                      onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                      className="pl-9"
                      data-testid="org-edit-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                    <Input
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="pl-9"
                      data-testid="org-edit-address"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Idle session timeout (minutes)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={480}
                    value={editForm.session_timeout_minutes}
                    onChange={(e) => setEditForm({ ...editForm, session_timeout_minutes: parseInt(e.target.value || '60', 10) })}
                    data-testid="org-edit-session-timeout"
                  />
                  <p className="text-xs text-slate-500">
                    Users are signed out after this many minutes of inactivity (5–480). Tellers on shared counters typically set 15–30 min; compliance officers, 120–240 min.
                  </p>
                </div>
                {user?.role === 'super_admin' && (
                  <div className="space-y-2">
                    <Label>MFA enforcement deadline (optional)</Label>
                    <Input
                      type="date"
                      value={editForm.mfa_enforcement_deadline || ''}
                      onChange={(e) => setEditForm({ ...editForm, mfa_enforcement_deadline: e.target.value })}
                      data-testid="org-edit-mfa-deadline"
                    />
                    <p className="text-xs text-slate-500">
                      After this date, any super_admin or org_admin in this org who has not enrolled in MFA will be <strong>blocked from logging in</strong> until an offline enrollment is completed. Leave blank to keep the current soft enforcement (forced enrollment page on next login).
                    </p>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button onClick={handleSaveOrg} disabled={savingOrg} data-testid="org-save-btn">
                    <Save className="w-4 h-4 mr-2" /> {savingOrg ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500">Contact Email</p>
                    <p className="font-medium text-slate-800">{org?.contact_email || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500">Contact Phone</p>
                    <p className="font-medium text-slate-800">{org?.contact_phone || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500">Address</p>
                    <p className="font-medium text-slate-800">{org?.address || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500">Billing Cycle</p>
                    <p className="font-medium text-slate-800 capitalize">{org?.billing_cycle || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-slate-500">Idle timeout</p>
                    <p className="font-medium text-slate-800">{(org?.session_timeout_minutes ?? 60)} min</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branches */}
        <Card data-testid="org-branches-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Branches</CardTitle>
              <CardDescription>{branches.length} branch{branches.length !== 1 ? 'es' : ''}</CardDescription>
            </div>
            {isOrgAdmin && (
              <Dialog open={showBranchDialog} onOpenChange={setShowBranchDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="org-add-branch-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Branch
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Branch</DialogTitle>
                    <DialogDescription>Create a new branch for your organization.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Branch Name</Label>
                      <Input
                        value={newBranch.name}
                        onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
                        placeholder="Kuala Lumpur HQ"
                        data-testid="branch-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Branch Code</Label>
                      <Input
                        value={newBranch.code}
                        onChange={(e) => setNewBranch({ ...newBranch, code: e.target.value })}
                        placeholder="KL-HQ"
                        data-testid="branch-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={newBranch.description}
                        onChange={(e) => setNewBranch({ ...newBranch, description: e.target.value })}
                        placeholder="Main office"
                        data-testid="branch-desc-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowBranchDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateBranch} disabled={!newBranch.name || !newBranch.code} data-testid="branch-create-btn">
                      Create Branch
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {branches.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No branches yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branches.map(b => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{b.code}</code></TableCell>
                      <TableCell className="text-slate-500">{b.description || '-'}</TableCell>
                      <TableCell>
                        <Badge className={b.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {b.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card data-testid="org-users-card">
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>{orgUsers.length} member{orgUsers.length !== 1 ? 's' : ''}</CardDescription>
          </CardHeader>
          <CardContent>
            {orgUsers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No team members yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgUsers.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.full_name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{u.role?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={u.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
